import React, { useState, useEffect } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom'; // Assuming React Router for slug and Link
import { useModal } from '../contexts/ModalContext'; // Added import

// Placeholder for fetching service data - replace with actual API call or data source
const fetchServiceData = async (serviceSlug) => {
  // Simulate API call
  await new Promise(resolve => setTimeout(resolve, 500));

  // Extended placeholder data for all services
  const allServicesData = {
    'air-conditioning': {
      slug: 'air-conditioning',
      title: 'Expert Air Conditioning Services in Houston',
      heroSubtitle: 'Fast, reliable AC repair, installation, and maintenance to keep your Houston home cool and comfortable all year round.',
      heroImage: '/images/services/ac-hero.jpg', // Replace with actual image path
      description: [
        'In the heart of Houston, a reliable air conditioning system isn\'t just a luxury—it\'s a necessity. Quality Air Houston specializes in comprehensive AC services, from emergency repairs to new system installations and preventative maintenance. Our certified technicians are equipped to handle all major brands and models, ensuring your home remains a cool oasis even during the hottest Texas months.',
        'We understand the urgency of a failing AC unit, which is why we prioritize prompt service and efficient solutions. Whether you\'re looking to upgrade to a more energy-efficient model or need a quick fix, we\'re here to provide expert advice and quality workmanship.'
      ],
      problems: [
        { id: 1, text: 'AC blowing warm or hot air', icon: '🌬️' }, // Placeholder icons
        { id: 2, text: 'Unit not turning on or frequently cycling', icon: '🔄' },
        { id: 3, text: 'Strange noises (grinding, squealing, banging)', icon: '🔊' },
        { id: 4, text: 'Water leaks around the indoor unit', icon: '💧' },
        { id: 5, text: 'Unexplained increase in energy bills', icon: '💸' },
        { id: 6, text: 'Uneven cooling or hot spots in your home', icon: '🌡️' }
      ],
      process: [
        { id: 1, title: 'Initial Consultation & System Diagnosis', description: 'We start with a thorough assessment of your current AC system or your cooling needs for a new installation.' },
        { id: 2, title: 'Transparent Quoting & Recommendations', description: 'You\'ll receive a clear, upfront quote with no hidden fees, and honest advice on the best course of action (repair vs. replacement).' },
        { id: 3, title: 'Professional Service by Certified Technicians', description: 'Our skilled technicians perform all repairs and installations to the highest industry standards.' },
        { id: 4, title: 'System Testing & Quality Assurance', description: 'We rigorously test the system post-service to ensure optimal performance and efficiency.' },
        { id: 5, title: 'Post-Service Support & Walkthrough', description: 'We\'ll walk you through the work done and provide tips for maintaining your system.' }
      ],
      benefits: [
        'Experienced & NATE-Certified AC Technicians',
        'Fast Response Times for AC Emergencies in Houston',
        'Upfront Pricing & Honest Recommendations',
        'High-Quality Parts & Equipment for Lasting Repairs',
        'Energy-Efficient AC System Installations & Upgrades',
        'Satisfaction Guaranteed on All AC Work'
      ],
      subServices: [
        { id: 1, name: 'AC Repair', description: 'Prompt and effective solutions for all AC problems.' },
        { id: 2, name: 'AC Installation & Replacement', description: 'Expert installation of new, high-efficiency air conditioning systems.' },
        { id: 3, name: 'AC Maintenance & Tune-Ups', description: 'Regular servicing to prevent breakdowns and improve efficiency.' },
        { id: 4, name: 'Ductless Mini-Split AC', description: 'Flexible cooling solutions for homes without ductwork.' },
        { id: 5, name: 'Smart Thermostat Installation', description: 'Upgrade your AC control for better efficiency and comfort.' }
      ],
      faqs: [
        { id: 1, question: 'How often should I service my AC in Houston?', answer: 'Due to the heavy usage in Houston, we recommend servicing your AC twice a year: once in the spring before summer, and once in the fall.' },
        { id: 2, question: 'What are the signs I might need a new AC unit?', answer: 'Frequent breakdowns, age (10-15+ years), significantly rising energy bills, and inconsistent cooling are common signs.' },
        { id: 3, question: 'How long does an AC installation take?', answer: 'A typical residential AC installation can take anywhere from 4 to 8 hours, depending on the complexity.' }
      ],
      ctaHeadline: 'Ready for a Cooler, More Comfortable Houston Home?',
      primaryCtaText: 'Schedule AC Service Now',
      primaryCtaLink: '/request-quote?service=ac',
      secondaryCtaText: 'Get an AC Installation Quote',
      secondaryCtaLink: '/request-quote?service=ac-installation',
      relatedTestimonials: [
        { id: 1, quote: 'Quality Air Houston fixed my AC in the middle of July! Lifesavers!', author: 'Sarah P., Houston' },
      ],
      relatedBlogPosts: [
        { id: 1, title: 'Top 5 Tips to Keep Your AC Running Efficiently This Summer', link: '/blog/ac-efficiency-tips' }
      ]
    },
    'heating': {
      slug: 'heating',
      title: 'Reliable Heating & Furnace Services in Houston',
      heroSubtitle: 'Stay warm and cozy with our expert furnace repair, installation, and maintenance services tailored for Houston homes.',
      heroImage: '/images/services/heating-hero.jpg',
      description: [
        'While Houston is known for its heat, reliable heating is crucial for those chilly days and nights. Quality Air Houston provides comprehensive heating services, including furnace repair, new furnace installation, heat pump solutions, and regular maintenance. Our goal is to ensure your home remains a warm sanctuary, efficiently and safely.',
        'Our certified technicians are proficient with all types of heating systems and are committed to providing timely service and durable solutions. We prioritize your safety and comfort, offering everything from emergency repairs to energy-efficient system upgrades.'
      ],
      problems: [
        { id: 1, text: 'Furnace not producing heat or blowing cold air', icon: '❄️' },
        { id: 2, text: 'Pilot light issues or ignition problems', icon: '🔥' },
        { id: 3, text: 'Strange noises from the furnace (rumbling, whistling)', icon: '🔊' },
        { id: 4, text: 'Increased energy bills during heating season', icon: '💸' },
        { id: 5, text: 'Unit cycling on and off too frequently', icon: '🔄' },
        { id: 6, text: 'Concerns about carbon monoxide (safety first!)', icon: '⚠️' }
      ],
      process: [
        { id: 1, title: 'Heating System Evaluation', description: 'A thorough inspection of your current heating system to diagnose issues or assess needs for a new installation.' },
        { id: 2, title: 'Clear Explanation & Quote', description: 'We provide a detailed explanation of the findings and a transparent quote for services.' },
        { id: 3, title: 'Professional Heating Service', description: 'Our technicians perform repairs or installations with precision and care, adhering to all safety codes.' },
        { id: 4, title: 'Safety & Performance Checks', description: 'We conduct comprehensive checks to ensure your heating system operates safely and efficiently.' },
        { id: 5, title: 'Customer Walkthrough & Advice', description: 'We explain the work completed and offer advice for optimal heating system performance.' }
      ],
      benefits: [
        'Certified & Experienced Heating Technicians',
        'Focus on Safe and Efficient Heating System Operation',
        'Upfront Pricing for All Furnace & Heat Pump Services',
        'Quality Parts for Dependable Heating Repairs',
        'Energy-Saving Heating System Installations',
        'Guaranteed Satisfaction on Heating Services'
      ],
      subServices: [
        { id: 1, name: 'Furnace Repair', description: 'Quick and reliable repairs for all furnace makes and models.' },
        { id: 2, name: 'Furnace Installation & Replacement', description: 'Expert installation of modern, high-efficiency furnaces.' },
        { id: 3, name: 'Heat Pump Services', description: 'Comprehensive repair, installation, and maintenance for heat pumps.' },
        { id: 4, name: 'Heating System Maintenance', description: 'Preventative tune-ups to ensure reliability and efficiency.' },
        { id: 5, name: 'Thermostat Services for Heating', description: 'Installation and repair of thermostats for precise heating control.' }
      ],
      faqs: [
        { id: 1, question: 'How long do furnaces typically last in Houston?', answer: 'A well-maintained furnace can last 15-20 years. Regular maintenance is key to longevity, especially with Houston\'s fluctuating usage.' },
        { id: 2, question: 'What\'s included in a heating system tune-up?', answer: 'A tune-up includes cleaning, inspection of all components, safety checks, and calibration for optimal performance.' },
        { id: 3, question: 'Is a heat pump a good option for Houston homes?', answer: 'Yes, heat pumps are very efficient for Houston\'s milder winters and also provide air conditioning in the summer.' }
      ],
      ctaHeadline: 'Ensure Your Houston Home Stays Warm & Safe?',
      primaryCtaText: 'Schedule Heating Service',
      primaryCtaLink: '/request-quote?service=heating',
      secondaryCtaText: 'Quote for New Heating System',
      secondaryCtaLink: '/request-quote?service=heating-installation',
      relatedTestimonials: [
        { id: 1, quote: 'Our furnace broke on a cold night, Quality Air Houston was there fast!', author: 'Mike B., Houston' },
      ],
      relatedBlogPosts: [
        { id: 1, title: 'Is Your Furnace Ready for Winter? A Checklist', link: '/blog/furnace-winter-checklist' }
      ]
    },
    'maintenance': {
        slug: 'maintenance',
        title: 'Proactive HVAC Maintenance Plans in Houston',
        heroSubtitle: 'Protect your investment, save on energy bills, and enjoy peace of mind with our comprehensive HVAC maintenance plans.',
        heroImage: '/images/services/maintenance-hero.jpg',
        description: [
            'Regular HVAC maintenance is key to ensuring your system runs efficiently and reliably throughout the year in Houston. Our maintenance plans are designed to catch small issues before they become big problems, extend the lifespan of your equipment, and keep your energy bills in check.',
            'Quality Air Houston offers tailored maintenance agreements that include regular inspections, cleaning, and tune-ups for both your heating and cooling systems. Become a plan member and enjoy priority service and discounts on repairs.'
        ],
        problemsItPrevents: [
            { id: 1, text: 'Unexpected and costly system breakdowns', icon: '🛠️' },
            { id: 2, text: 'High energy bills due to inefficient operation', icon: '💡' },
            { id: 3, text: 'Shortened HVAC system lifespan', icon: '⏳' },
            { id: 4, text: 'Poor indoor air quality from dirty filters/coils', icon: '🍃' },
            { id: 5, text: 'Voiding manufacturer warranties due to neglect', icon: '📄' },
        ],
        planTiers: [
            {
                id: 1, name: 'Essential Care Plan',
                features: ['2 Seasonal Tune-Ups (Spring AC & Fall Heating)', 'Standard Filter Replacement', 'System Cleaning & Inspection', '10% Discount on Repairs']
            },
            {
                id: 2, name: 'Comfort Plus Plan',
                features: ['All Essential Care benefits', 'Priority Service Scheduling', 'No Overtime Charges for Emergency Repairs', '15% Discount on Repairs', 'Condensate Drain Treatment']
            }
        ],
        benefits: [
            'Extend the Lifespan of Your HVAC Equipment',
            'Lower Monthly Utility Bills through Improved Efficiency',
            'Prevent Costly Emergency Repairs',
            'Improve Overall System Performance and Comfort',
            'Maintain Healthy Indoor Air Quality',
            'Priority Service and Discounts for Plan Members'
        ],
        faqs: [
            { id: 1, question: 'What\'s typically checked during an HVAC maintenance visit?', answer: 'Technicians inspect electrical connections, clean coils, check refrigerant levels, test thermostats, inspect ductwork (visual), lubricate moving parts, and much more.' },
            { id: 2, question: 'Is an HVAC maintenance plan really worth it in Houston?', answer: 'Absolutely. Given the high usage of AC and heating systems in Houston, preventative maintenance can save you significant money on repairs and energy costs, and prolong system life.' },
            { id: 3, question: 'How often are maintenance visits scheduled?', answer: 'Our plans typically include two visits per year, one to prepare your AC for summer and one to prepare your heating system for winter.' }
        ],
        ctaHeadline: 'Invest in Your Comfort & System Longevity',
        primaryCtaText: 'Explore Maintenance Plans',
        primaryCtaLink: '/request-quote?service=maintenance-plan-signup',
        secondaryCtaText: 'Ask About Our Plans',
        secondaryCtaLink: '/contact-us?query=maintenance-plans',
    },
    'indoor-air-quality': {
        slug: 'indoor-air-quality',
        title: 'Breathe Easier with Indoor Air Quality Solutions in Houston',
        heroSubtitle: "Improve your Houston home's health and comfort with our advanced IAQ services, including air purification, humidity control, and ventilation.",
        heroImage: '/images/services/iaq-hero.jpg',
        description: [
            "Breathe cleaner, healthier air with Quality Air Houston's comprehensive Indoor Air Quality (IAQ) solutions. We help Houston homeowners identify and address airborne pollutants, allergens, and humidity imbalances that can impact health and comfort.",
            "Our IAQ services range from advanced air purification systems and humidity control to duct cleaning and ventilation, tailored to your specific needs."
        ],
        problemsAddressed: [
            { id: 1, text: 'Persistent allergies or respiratory issues indoors', icon: '🤧' },
            { id: 2, text: 'Excessive dust accumulation', icon: '💨' },
            { id: 3, text: 'Unpleasant odors or musty smells', icon: '👃' },
            { id: 4, text: 'Visible mold or mildew growth', icon: '🍄' },
            { id: 5, text: 'Stuffy or stale air', icon: '🌬️' },
            { id: 6, text: 'Excessive humidity or dryness', icon: '💧' }
        ],
        solutionsOffered: [
            { id: 1, name: 'Whole-Home Air Purification Systems', description: 'Advanced filtration to remove airborne particles, allergens, and pathogens.' },
            { id: 2, name: 'Humidifiers & Dehumidifiers', description: "Control your home\'s humidity levels for optimal comfort and health."},
            { id: 3, name: 'Energy Recovery Ventilators (ERVs)', description: 'Bring in fresh, filtered air while expelling stale air and retaining energy.' },
            { id: 4, name: 'Duct Cleaning & Sealing', description: 'Remove contaminants from ductwork and improve system efficiency.' },
            { id: 5, name: 'UV Germicidal Lights', description: 'Kill mold, bacteria, and viruses on AC coils and in the airstream.' },
            { id: 6, name: 'High-Efficiency Air Filters', description: 'Upgrade your standard filters for better particle capture.' }
        ],
        benefits: [
            'Reduce Allergens, Pollen, and Dust Mites',
            'Alleviate Asthma and Respiratory Symptoms',
            'Eliminate Unpleasant Odors from Pets, Cooking, etc.',
            'Control Humidity to Prevent Mold Growth & Improve Comfort',
            'Create a Healthier Overall Living Environment for Your Family',
            'Improve HVAC System Efficiency with Cleaner Components'
        ],
        faqs: [
            { id: 1, question: 'What are common signs of poor indoor air quality in Houston?', answer: 'Increased allergy symptoms, persistent odors, excessive dust, and visible mold are common indicators. Humidity issues are also prevalent in Houston.' },
            { id: 2, question: 'How do whole-home air purifiers work?', answer: 'They integrate with your HVAC system to filter or neutralize pollutants from the air circulating throughout your entire home, rather than just a single room.' },
            { id: 3, question: 'How often should I change my air filter for better IAQ?', answer: 'For basic 1-inch filters, every 1-3 months is recommended. Higher efficiency filters may last longer, but it depends on your home environment. We can advise on the best schedule.' }
        ],
        ctaHeadline: 'Ready for Cleaner, Healthier Air in Your Houston Home?',
        primaryCtaText: 'Request IAQ Consultation',
        primaryCtaLink: '/request-quote?service=iaq-consultation',
        secondaryCtaText: 'Learn About Air Purifiers',
        secondaryCtaLink: '/contact-us?query=air-purifiers'
    },
    'commercial-hvac': {
        slug: 'commercial-hvac',
        title: 'Expert Commercial HVAC Services for Houston Businesses',
        heroSubtitle: 'Reliable and efficient HVAC solutions to keep your Houston business running smoothly, ensuring comfort for employees and customers.',
        heroImage: '/images/services/commercial-hero.jpg',
        description: [
            'Quality Air Houston understands that a functional HVAC system is critical for your business operations. We offer comprehensive commercial HVAC services in Houston, catering to a wide range of businesses including offices, retail spaces, restaurants, and more. Our goal is to provide solutions that minimize downtime, optimize energy usage, and create a comfortable environment.',
            'From routine maintenance and emergency repairs of rooftop units and complex systems to new installations and retrofits, our experienced commercial technicians are equipped to handle the unique demands of commercial HVAC.'
        ],
        problemsAddressed: [
            { id: 1, text: 'HVAC system breakdowns impacting business operations', icon: '🏢' },
            { id: 2, text: 'High energy costs for heating and cooling large spaces', icon: '💰' },
            { id: 3, text: 'Inconsistent temperatures or comfort complaints', icon: '🌡️' },
            { id: 4, text: 'Poor air quality affecting employee productivity', icon: '😷' },
            { id: 5, text: 'Aging or inefficient commercial HVAC equipment', icon: '🛠️' }
        ],
        subServices: [
            { id: 1, name: 'Commercial HVAC Repair', description: 'Fast and reliable repairs for all types of commercial systems, including rooftop units and VRF systems.' },
            { id: 2, name: 'Commercial HVAC Installation & Replacement', description: 'Expert design and installation of energy-efficient HVAC systems for new constructions or upgrades.' },
            { id: 3, name: 'Commercial HVAC Maintenance Contracts', description: 'Customized preventative maintenance plans to ensure system reliability and efficiency.' },
            { id: 4, name: 'System Design & Retrofitting', description: 'Optimizing existing systems or designing new ones for maximum performance and energy savings.' },
            { id: 5, name: 'Specialized Solutions (e.g., Server Room Cooling)', description: 'Tailored HVAC for specific commercial needs.' }
        ],
        benefits: [
            'Minimize Business Downtime with Prompt HVAC Service',
            'Reduce Operating Costs through Energy-Efficient Solutions',
            'Customized HVAC Solutions for Your Specific Business Needs',
            'Experienced & Licensed Commercial HVAC Technicians',
            'Comprehensive Maintenance to Prevent Costly Issues',
            'Improved Comfort for Employees and Customers'
        ],
        faqs: [
            { id: 1, question: 'What types of commercial buildings do you service in Houston?', answer: 'We service a wide range, including office buildings, retail stores, restaurants, warehouses, medical offices, and more. Contact us to discuss your specific needs.' },
            { id: 2, question: 'Do you offer emergency commercial HVAC repair services?', answer: 'Yes, we understand the urgency of commercial HVAC issues and offer emergency repair services to get your business back up and running quickly.' },
            { id: 3, question: 'How are commercial HVAC maintenance plans different from residential?', answer: 'Commercial plans are tailored to the larger, more complex systems and higher usage demands of businesses. They often involve more frequent checks and specialized component servicing.' }
        ],
        ctaHeadline: 'Keep Your Houston Business Comfortable & Productive',
        primaryCtaText: 'Request Commercial HVAC Service',
        primaryCtaLink: '/request-quote?service=commercial-hvac-service',
        secondaryCtaText: 'Get a Commercial HVAC Quote',
        secondaryCtaLink: '/request-quote?service=commercial-hvac-quote'
    }
  };

  return allServicesData[serviceSlug] || null;
};

// Simple Accordion Component (can be moved to a separate file)
const AccordionItem = ({ title, children }) => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div className="border-b border-gray-200">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex justify-between items-center w-full py-4 text-left text-brand-gray-dark hover:text-brand-green focus:outline-none"
      >
        <span className="font-semibold">{title}</span>
        <span>{isOpen ? '-' : '+'}</span>
      </button>
      {isOpen && <div className="pb-4 text-brand-gray">{children}</div>}
    </div>
  );
};

const ServiceDetailPage = () => {
  const { serviceSlug } = useParams();
  const [serviceData, setServiceData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { openModal } = useModal(); // Get openModal function

  useEffect(() => {
    const loadService = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await fetchServiceData(serviceSlug);
        if (data) {
          setServiceData(data);
        } else {
          setError('Service not found.');
        }
      } catch (err) {
        setError('Failed to load service information.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    loadService();
  }, [serviceSlug]);

  if (loading) {
    return (
        <div className="flex justify-center items-center min-h-screen">
            {/* You can use a spinner component here */}
            <p className="text-xl text-brand-gray">Loading service details...</p>
        </div>
    );
  }

  if (error) {
    return (
        <div className="container mx-auto px-4 py-16 text-center">
            <h1 className="text-2xl text-red-600">Error</h1>
            <p className="text-brand-gray mt-4">{error}</p>
            <RouterLink to="/services" className="mt-8 inline-block bg-brand-green text-white py-2 px-4 rounded hover:bg-brand-green-dark">
                Back to Services
            </RouterLink>
        </div>
    );
  }

  if (!serviceData) {
    // This case should ideally be handled by the error state if service not found
    return (
        <div className="container mx-auto px-4 py-16 text-center">
            <h1 className="text-2xl text-brand-gray-dark">Service Not Found</h1>
            <p className="text-brand-gray mt-4">The service you are looking for does not exist.</p>
            <RouterLink to="/services" className="mt-8 inline-block bg-brand-green text-white py-2 px-4 rounded hover:bg-brand-green-dark">
                Back to Services
            </RouterLink>
        </div>
    );
  }

  // Destructure for easier access, using defaults for optional fields
  const {
    title, heroSubtitle, heroImage, description = [],
    problems = [], process = [], benefits = [], subServices = [], faqs = [],
    ctaHeadline, primaryCtaText, /* primaryCtaLink removed */ secondaryCtaText, /* secondaryCtaLink removed */
    relatedTestimonials = [], relatedBlogPosts = [],
    problemsItPrevents = [], // For maintenance page
    planTiers = [], // For maintenance page
    solutionsOffered = [] // For IAQ page
  } = serviceData;

  return (
    <div className="bg-brand-background min-h-screen">
      {/* Breadcrumbs -  Can be a separate component */}
      <nav className="bg-gray-100 py-3">
        <div className="container mx-auto px-4 text-sm text-brand-gray">
          <RouterLink to="/" className="hover:text-brand-green">Home</RouterLink>
          <span className="mx-2">&gt;</span>
          <RouterLink to="/services" className="hover:text-brand-green">Services</RouterLink>
          <span className="mx-2">&gt;</span>
          <span className="text-brand-gray-dark">{title}</span>
        </div>
      </nav>

      {/* Main Header / Hero Section */}
      <header 
        className="relative py-16 md:py-24 bg-gray-700 text-white bg-no-repeat bg-cover bg-center"
        style={{ backgroundImage: heroImage ? `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url(${heroImage})` : 'none' }}
      >
        <div className="container mx-auto px-4 relative z-10 text-center">
          <h1 className="text-3xl md:text-5xl font-bold mb-4">{title}</h1>
          {heroSubtitle && <p className="text-lg md:text-xl mb-8 max-w-3xl mx-auto">{heroSubtitle}</p>}
          {primaryCtaText && (
            <button
              onClick={() => openModal('scheduleService')}
              className="cta-button text-lg sm:text-xl py-3 px-8 sm:py-4 sm:px-10 shadow-sm hover:shadow-md transition-shadow"
            >
              {primaryCtaText.startsWith("Schedule") ? (
                <><span className="text-black">{primaryCtaText.split(" ")[0]}</span> {primaryCtaText.substring(primaryCtaText.indexOf(" ") + 1)}</>
              ) : (
                primaryCtaText
              )}
            </button>
          )}
        </div>
      </header>

      <main className="container mx-auto px-4 py-12 md:py-16">
        {/* Detailed Service Description Section */}
        {description.length > 0 && (
          <section className="mb-12 md:mb-16">
            <h2 className="text-2xl md:text-3xl font-semibold text-brand-gray-dark mb-6 text-center">About Our {serviceData.slug.replace('-', ' ')} Services</h2>
            <div className="prose prose-lg max-w-none text-brand-gray space-y-4">
              {description.map((paragraph, index) => <p key={index}>{paragraph}</p>)}
            </div>
          </section>
        )}
        
        {/* Common Problems Addressed / When Do You Need This Service? */}
        {(problems.length > 0 || problemsItPrevents.length > 0) && (
          <section className="mb-12 md:mb-16 bg-brand-green-lightest p-6 md:p-10 rounded-lg">
            <h2 className="text-2xl md:text-3xl font-semibold text-brand-gray-dark mb-6 text-center">
              {serviceData.slug === 'maintenance' ? 'Common Issues Our Maintenance Plans Prevent' : `When Do You Need ${title}?`}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {(serviceData.slug === 'maintenance' ? problemsItPrevents : problems).map(item => (
                <div key={item.id} className="bg-white p-4 rounded-md shadow flex items-start">
                  {item.icon && <span className="text-2xl mr-3 pt-1">{item.icon}</span>}
                  <p className="text-brand-gray">{item.text}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Our Process / What to Expect (Not applicable to maintenance directly, but can be adapted) */}
        {process.length > 0 && serviceData.slug !== 'maintenance' && serviceData.slug !== 'indoor-air-quality' && (
          <section className="mb-12 md:mb-16">
            <h2 className="text-2xl md:text-3xl font-semibold text-brand-gray-dark mb-8 text-center">Our {serviceData.slug.replace('-', ' ')} Process</h2>
            <div className="space-y-6">
              {process.map((step, index) => (
                <div key={step.id} className="flex items-start p-4 bg-white rounded-lg shadow">
                  <div className="flex-shrink-0 bg-brand-green text-white rounded-full h-10 w-10 flex items-center justify-center font-bold text-lg mr-4">
                    {index + 1}
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-brand-gray-dark mb-1">{step.title}</h3>
                    <p className="text-brand-gray">{step.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Plan Tiers (for Maintenance Page) */}
        {planTiers.length > 0 && serviceData.slug === 'maintenance' && (
            <section className="mb-12 md:mb-16">
                <h2 className="text-2xl md:text-3xl font-semibold text-brand-gray-dark mb-8 text-center">Our Maintenance Plan Options</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {planTiers.map(plan => (
                        <div key={plan.id} className="bg-white p-6 rounded-lg shadow-lg border border-brand-green">
                            <h3 className="text-xl font-bold text-brand-green mb-4">{plan.name}</h3>
                            <ul className="space-y-2 list-disc list-inside text-brand-gray">
                                {plan.features.map((feature, idx) => <li key={idx}>{feature}</li>)}
                            </ul>
                        </div>
                    ))}
                </div>
            </section>
        )}

        {/* Solutions Offered (for IAQ Page) */}
        {solutionsOffered.length > 0 && serviceData.slug === 'indoor-air-quality' && (
            <section className="mb-12 md:mb-16">
                <h2 className="text-2xl md:text-3xl font-semibold text-brand-gray-dark mb-8 text-center">Our Indoor Air Quality Solutions</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {solutionsOffered.map(solution => (
                        <div key={solution.id} className="bg-white p-6 rounded-lg shadow">
                            <h3 className="text-lg font-semibold text-brand-gray-dark mb-2">{solution.name}</h3>
                            <p className="text-brand-gray text-sm">{solution.description}</p>
                        </div>
                    ))}
                </div>
            </section>
        )}

        {/* Benefits of Choosing Quality Air Houston */}
        {benefits.length > 0 && (
          <section className="mb-12 md:mb-16 bg-gray-50 p-6 md:p-10 rounded-lg">
            <h2 className="text-2xl md:text-3xl font-semibold text-brand-gray-dark mb-6 text-center">Benefits of Choosing Quality Air Houston</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-4">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-start">
                  <svg className="flex-shrink-0 h-6 w-6 text-brand-green mr-2 mt-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <p className="text-brand-gray">{benefit}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Specific Sub-Services Offered (Not for Maintenance/IAQ which have their own specific sections) */}
        {subServices.length > 0 && serviceData.slug !== 'maintenance' && serviceData.slug !== 'indoor-air-quality' && (
          <section className="mb-12 md:mb-16">
            <h2 className="text-2xl md:text-3xl font-semibold text-brand-gray-dark mb-8 text-center">Specific {serviceData.slug.replace('-', ' ')} Services We Offer</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {subServices.map(subService => (
                <div key={subService.id} className="bg-white p-6 rounded-lg shadow hover:shadow-md transition-shadow">
                  <h3 className="text-lg font-semibold text-brand-gray-dark mb-2">{subService.name}</h3>
                  <p className="text-sm text-brand-gray">{subService.description}</p>
                  {/* Optionally add a link here if sub-services have even more detailed pages */}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Related FAQs */}
        {faqs.length > 0 && (
          <section className="mb-12 md:mb-16 max-w-3xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-semibold text-brand-gray-dark mb-8 text-center">Frequently Asked Questions</h2>
            <div className="space-y-2">
              {faqs.map(faq => (
                <AccordionItem key={faq.id} title={faq.question}>
                  {faq.answer}
                </AccordionItem>
              ))}
            </div>
          </section>
        )}

        {/* Strong CTA Section */}
        <section className="py-12 md:py-20 bg-brand-green-light">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-brand-gray-dark mb-4">{ctaHeadline}</h2>
            <p className="text-lg text-brand-gray mb-8 max-w-2xl mx-auto">
              Contact us today to learn more or to schedule your service with our expert Houston team.
            </p>
            <div className="flex flex-col sm:flex-row justify-center items-center gap-4">
              {primaryCtaText && (
                <button
                  onClick={() => openModal('scheduleService')}
                  className="bg-brand-accent hover:bg-brand-accent-dark text-white font-semibold py-3 px-8 rounded-lg shadow-lg transition duration-300 ease-in-out text-lg"
                >
                  {primaryCtaText.startsWith("Schedule") ? (
                    <><span className="text-black">{primaryCtaText.split(" ")[0]}</span> {primaryCtaText.substring(primaryCtaText.indexOf(" ") + 1)}</>
                  ) : (
                    primaryCtaText
                  )}
                </button>
              )}
              {secondaryCtaText && (
                <button
                  onClick={() => openModal('scheduleService')} // Or a different modal if secondary CTA is different
                  className="bg-brand-green hover:bg-brand-green-dark text-white font-semibold py-3 px-8 rounded-lg shadow-lg transition duration-300 ease-in-out text-lg"
                >
                  {secondaryCtaText}
                </button>
              )}
            </div>
          </div>
        </section>

        {/* Optional: Related Testimonials Snippet */}
        {relatedTestimonials.length > 0 && (
          <section className="py-12 md:py-16">
            <div className="container mx-auto px-4 max-w-3xl text-center">
              <h2 className="text-2xl font-semibold text-brand-gray-dark mb-8">What Our Clients Say</h2>
              {relatedTestimonials.map(testimonial => (
                <blockquote key={testimonial.id} className="mb-6 p-4 bg-gray-50 rounded-lg italic">
                  <p className="text-brand-gray">"{testimonial.quote}"</p>
                  <cite className="block text-right text-sm text-brand-gray-light mt-2 not-italic">- {testimonial.author}</cite>
                </blockquote>
              ))}
              <RouterLink to="/testimonials" className="text-brand-green hover:text-brand-green-dark font-semibold">
                View More Testimonials &rarr;
              </RouterLink>
            </div>
          </section>
        )}

        {/* Optional: Link to Related Blog Posts */}
        {relatedBlogPosts.length > 0 && (
          <section className="pb-12 md:pb-16 pt-8 bg-gray-50">
            <div className="container mx-auto px-4 max-w-3xl text-center">
              <h2 className="text-2xl font-semibold text-brand-gray-dark mb-8">Further Reading</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {relatedBlogPosts.map(post => (
                  <RouterLink key={post.id} to={post.link} className="block p-4 bg-white rounded-lg shadow hover:shadow-md text-brand-green hover:text-brand-green-dark">
                    <h3 className="font-semibold mb-1">{post.title}</h3>
                    <p className="text-sm text-brand-gray-light">Read more &rarr;</p>
                  </RouterLink>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
    </div>
  );
};

export default ServiceDetailPage; 